/**
 * Mobile App Asset Hosting Schemas
 *
 * Types for the dynamic-fetch brand-asset hosting flow:
 * - per-slot upload signature generation (Cloudinary signed widgets)
 * - commit endpoint that registers an uploaded asset against a slot
 * - public manifest endpoint consumed by the mobile bootstrap layer
 *
 * Canonical slot inventory lives below as `MOBILE_APP_ASSET_SLOTS` —
 * imported by the backend's `validateUploadedAsset` and by the web admin's
 * slot-card UI. The validation function itself stays backend-only since it
 * uses neverthrow Result.
 */

import { z } from 'zod'

// =============================================================================
// Canonical slot inventory
// =============================================================================

export type AssetSlotKind = 'build' | 'runtime' | 'store'

/**
 * Whether replacing this slot's image actually reaches a device — the answer
 * the per-app history has to state, because "the logo changed" reads identically
 * whether devices got it or not (plan 2026-08-15-001 U17, OQ3 answered
 * 2026-08-16 against the live code).
 *
 *  - `live`        : reaches devices at runtime, so the change is on devices with
 *                    no rebuild. Two readers qualify a slot: the post-login
 *                    projection into `appConfig.assets` by
 *                    `build-app-config-assets-from-slots.ts` (served on
 *                    `GET /v1/schools/:id?app_config=true`), and the PRE-login
 *                    lane — the logo, read from the public asset manifest and
 *                    rendered on the next cold start (plan 2026-09-01-2310 U10),
 *                    and the four onboarding/login images, served in the
 *                    `assets` object of the PUBLIC onboarding-config response
 *                    and read by app 1.9 and later (plan 2026-09-01-001 U5).
 *  - `ota`         — carried by the JS bundle, so an over-the-air update from
 *                    the app's release line takes it to installed apps. No
 *                    slot is `ota` on its own: the verdict depends on which
 *                    line the app runs, and the per-line answer lives in the
 *                    school field catalog (`school-app-field-delivery.ts`,
 *                    release lines U7, KTD5), which extends this vocabulary
 *                    rather than keeping a parallel one.
 *  - `store-build` — baked into the binary. The change reaches devices only
 *                    through a new store build.
 *  - `none`        — committed and stored, but nothing reads it: no runtime
 *                    projection and no build-time consumer. The upload
 *                    succeeded and changed nothing a user can see.
 *  - `store-listing` — stored for a STORE LISTING, never for a device. It is
 *                    not projected into app config, not in the pre-login
 *                    response, not in the snapshot bundle and not baked into
 *                    any binary. Deliberately NOT `none`: `none` says "nothing
 *                    reads this slot", which is true today only because the
 *                    browser-runner Play upload still reads the operator's
 *                    local `--listing-dir`. When that follow-up reads this slot
 *                    instead, `none` becomes a lie and this verdict does not.
 *
 * `kind` is NOT a substitute for this. Every `build` slot is `store-build`, but
 * Every `runtime` slot is currently `live`: the logo slots through the app
 * config and the pre-login logo lane, and `onboard-1/2/3` + `login-bg` through
 * the public pre-login onboarding-config response (app 1.9 and later). The two
 * axes are still independent — a `runtime` slot with no reader would be `none`,
 * and collapsing the two is exactly the mistake this field exists to prevent.
 */
export type AssetSlotDelivery = 'live' | 'ota' | 'store-build' | 'none' | 'store-listing'

/** {@link AssetSlotDelivery}, plus the answer for a slot name no longer in the inventory. */
export type AssetSlotDeliveryVerdict = AssetSlotDelivery | 'unknown'

export interface AssetSlotMetadata {
  readonly name: string
  /**
   * What an operator sees on the slot card. Present only where the storage
   * key would mislead: `logo-small-light` is THE logo since the 2026-09-02
   * collapse, but its key still says "small" because renaming a slot means
   * moving every committed row. The `main-logo-*` pair carries one for the
   * same reason — the key says "main", the slot is the login-only wide mark.
   * Absent ⇒ the UI shows `name`.
   */
  readonly label?: string
  readonly kind: AssetSlotKind
  readonly delivery: AssetSlotDelivery
  readonly required: boolean
  readonly acceptedMimeTypes: readonly string[]
  readonly minWidth?: number
  readonly maxWidth?: number
  readonly minHeight?: number
  readonly maxHeight?: number
  /**
   * When true, width must equal height. Lets a slot accept a *range* of square
   * sizes (e.g. adaptive-icon-foreground: 432–1024) without an independent
   * width/height range silently admitting non-square shapes (e.g. 432×1024).
   * Dimension validators skip this check when either dimension is absent
   * (SVG uploads carry no raster dimensions).
   */
  readonly requireSquare?: boolean
  /**
   * When true, the image must carry no transparency — JPEG, or a 24-bit
   * (colour type 2) PNG with no `tRNS` chunk. Google Play rejects a feature
   * graphic with an alpha channel, and a browser's own PNG export always
   * writes one, so the rule has to be stated rather than assumed.
   *
   * ENFORCED IN THE WEB CLIENT ONLY (SCH-1866 KTD7): the super-admin upload
   * mutation reads the file's bytes before the signed upload. The server does
   * not re-read the pixels — the super-admin and internal commit lanes trust
   * the caller's metadata, as they already do for every other slot. Treat this
   * as a guard against an operator mistake, not as a guarantee.
   */
  readonly requireOpaque?: boolean
  readonly maxSizeBytes: number
  readonly purpose: string
}

const MIME_RASTER = ['image/png', 'image/jpeg', 'image/webp'] as const
const MIME_LOGO_PNG_ONLY = ['image/png', 'image/webp'] as const
/** Play accepts JPEG or 24-bit PNG for listing art; WebP is not a Play format. */
const MIME_STORE_GRAPHIC = ['image/jpeg', 'image/png'] as const

const MAX_SIZE_LOGO = 2 * 1024 * 1024 // 2 MB
// 10 MB was never a considered figure, and these are fetched over cellular on a cold start, so
// the ceiling is a real user cost rather than just a validation rule. 4 MB is the honest floor:
// the allowed maximum dimensions (4096×6000) put a JPEG or WebP photo at roughly 1.5–3 MB, and
// Westminster's shipped 2344×5064 login background sits in that band — a 2 MB cap would refuse
// files we already serve. Enforced on UPLOAD only; rows already above it keep working.
const MAX_SIZE_BG = 4 * 1024 * 1024 // 4 MB
const MAX_SIZE_ICON = 1 * 1024 * 1024 // 1 MB
// 4 MB is the ceiling for every image slot. The Play feature graphic is a fixed 1024×500, which
// is well under a megabyte in practice — 15 MB only ever admitted a mistake.
const MAX_SIZE_STORE_GRAPHIC = 4 * 1024 * 1024 // 4 MB
// Google Play's own cap on a screenshot file.
// NOT lowered to the 4 MB ceiling: 8 MB is Play's own per-image rule for a listing screenshot,
// which `store-screenshots.ts` enforces separately on the hand-upload path. A slot stricter than
// the store it feeds refuses art Play would accept, and these slots are written by the generator
// rather than by hand, so nobody would be there to see why.
const MAX_SIZE_PLAY_SCREENSHOT = 8 * 1024 * 1024 // 8 MB

/**
 * Canonical mobile-app asset slots.
 *
 * Build slots (3): baked into the binary; managed by the U4 fallback
 * assets, NOT dynamic-fetch. The web admin UI surfaces them as read-only.
 *
 * Runtime slots (8): dynamic-fetch via Cloudinary; the scope of U2/U3/U5.
 * Four logo slots: the square `logo-small-*` pair every surface renders, plus
 * the optional, login-only `main-logo-*` wide pair restored by SCH-1336 (see
 * the notes above `logo-small-light` and `main-logo-light`).
 *
 * Store slots (1): store-listing art, stored but never delivered to a device
 * (SCH-1866).
 *
 * Source: docs/_archive/2026-10-17-plans-sweep/plans/2026-05-09-002-feat-mobile-asset-hosting-plan.md →
 * "Canonical slot inventory" (audit of 7 production schools, 2026-05-09).
 */
export const MOBILE_APP_ASSET_SLOTS = {
  // ===== Build slots =====
  'app-icon': {
    name: 'app-icon',
    kind: 'build',
    delivery: 'store-build',
    required: true,
    acceptedMimeTypes: MIME_LOGO_PNG_ONLY,
    // KEEP STRICT (exact 1024×1024): Apple requires exactly 1024×1024 for App
    // Store submission. Do NOT loosen to accommodate off-spec production files
    // — those must be resized instead. Offenders flagged by the migration
    // dry-run go on the manual-resize list (SCH-135 PR body).
    minWidth: 1024,
    maxWidth: 1024,
    minHeight: 1024,
    maxHeight: 1024,
    maxSizeBytes: MAX_SIZE_ICON,
    purpose: 'iOS + Android app icon (baked; store resubmission required to change)'
  },
  'adaptive-icon-foreground': {
    name: 'adaptive-icon-foreground',
    kind: 'build',
    delivery: 'store-build',
    required: true,
    acceptedMimeTypes: MIME_LOGO_PNG_ONLY,
    // RANGE 432–1024, square. Android's native adaptive-icon foreground spec is
    // 432×432 px (108dp @ xxxhdpi) — 432px files are essentially spec-correct
    // and have shipped in store builds for months. Expo recommends 1024 and
    // scales whatever it gets, so accept the whole [432,1024] square band rather
    // than the old exact-1024 rule that rejected production-shipped 434px files.
    // `requireSquare` keeps the band square (a plain min/max range would admit
    // non-square shapes like 432×1024). Unlike app-icon (Apple-mandated exact
    // 1024 — KEEP STRICT), Android imposes no exact-size requirement here.
    minWidth: 432,
    maxWidth: 1024,
    minHeight: 432,
    maxHeight: 1024,
    requireSquare: true,
    maxSizeBytes: MAX_SIZE_ICON,
    purpose: 'Android adaptive icon foreground layer (baked; 432–1024 square)'
  },
  splash: {
    name: 'splash',
    kind: 'build',
    delivery: 'store-build',
    required: true,
    acceptedMimeTypes: MIME_LOGO_PNG_ONLY,
    // Shipped store assets are 2344×5064 (tall full-bleed splash, all 7
    // production schools). Height cap raised from 4096 → 6000 to fit reality
    // plus headroom; width cap stays 4096 (max observed 2344).
    minWidth: 512,
    maxWidth: 4096,
    minHeight: 512,
    maxHeight: 6000,
    maxSizeBytes: MAX_SIZE_BG,
    purpose: 'Native splash screen image (baked; first paint before JS bridge ready)'
  },

  // ===== Runtime slots =====
  //
  // ONE LOGO (plan 2026-09-02 logo-slot-collapse). The registry used to ask for
  // five logo files — `main-logo-light`, `main-logo-dark`, `main-logo-png`,
  // `logo-small-light`, `logo-small-dark` — each a fix for one screen. Every
  // logo spot in the app is a square box that scales a mark with
  // `contentFit: contain` (88pt on the login screen, 48pt in the pre-login
  // dialogs, 28–32pt in the post-login headers), so one square raster mark
  // serves all of them. The `logo-small-*` pair is the pair that survived: it
  // was already PNG-only and already square at every school, and neither
  // runtime lane (the pre-login cache, `expo-image` post-login) can render the
  // SVGs the `main-logo-*` slots accepted. The STORAGE KEYS are unchanged so no
  // `MobileAppAssetSlot` row had to move; `label` is what operators see.
  // Rows for the three removed slots survived in the table and were ignored
  // everywhere, exactly like the `home-bg-*` rows U24 left behind — until
  // SCH-1336 brought two of them back for the login screen alone (see the
  // `main-logo-*` note below). `main-logo-png` stays gone.
  'logo-small-light': {
    name: 'logo-small-light',
    label: 'Logo',
    kind: 'runtime',
    delivery: 'live',
    required: true,
    acceptedMimeTypes: MIME_LOGO_PNG_ONLY,
    // Square so the mark reads at 28pt as well as 88pt. 256 is the floor the
    // login screen needs (88pt × 3); 1024 is the recommendation. The maximum
    // matches the other square slots.
    minWidth: 256,
    maxWidth: 4096,
    minHeight: 256,
    maxHeight: 4096,
    requireSquare: true,
    maxSizeBytes: MAX_SIZE_LOGO,
    purpose:
      'The school mark. One square PNG, 1024 px recommended — rendered at every size from the header to the login screen.'
  },
  'logo-small-dark': {
    name: 'logo-small-dark',
    label: 'Logo on dark backgrounds',
    kind: 'runtime',
    delivery: 'live',
    required: false,
    acceptedMimeTypes: MIME_LOGO_PNG_ONLY,
    minWidth: 256,
    maxWidth: 4096,
    minHeight: 256,
    maxHeight: 4096,
    requireSquare: true,
    maxSizeBytes: MAX_SIZE_LOGO,
    purpose: 'Optional. Only if the mark disappears on dark backgrounds; otherwise the Logo is used in dark mode too.'
  },
  // ===== The wide login mark (SCH-1336) =====
  //
  // RESTORED, NOT UN-COLLAPSED. The 2026-09-02 collapse was right that every
  // logo spot in the app is a SQUARE box — except one. Six schools drew a
  // non-square mark on the login screen (School Journey's stacked wordmark,
  // Westminster's wide W, King's Ridge's crest+wordmark, GISSV's portrait,
  // Montgomery Bell's monogram, Idyllwild's wordmark) and lost it when
  // `main-logo-*` left the inventory; the square mark it fell back to reads as
  // a different brand at 88pt. So the pair comes back under its ORIGINAL
  // storage keys — the `MobileAppAssetSlot` rows and Cloudinary objects never
  // moved — scoped to the one spot that needs it:
  //
  //  - OPTIONAL. A school with no wide mark simply keeps the square one.
  //  - LOGIN-ONLY. `logos.large` in the app-config projection and the
  //    `size="large"` branch of the mobile pre-login lane read it; headers,
  //    dialogs and gates keep the square mark.
  //  - PNG/WebP, never SVG. Neither runtime reader can decode SVG — that is
  //    why the original slots' SVG uploads never reached a device, and the
  //    reason `main-logo-png` is NOT coming back: it existed only to work
  //    around the SVG the other two accepted.
  //
  // NO `requireSquare`: wide is the entire point, so the admin's upload dialog
  // takes these two free-aspect (no cropper) rather than exporting a square.
  'main-logo-light': {
    name: 'main-logo-light',
    label: 'Login logo (wide)',
    kind: 'runtime',
    delivery: 'live',
    required: false,
    acceptedMimeTypes: MIME_LOGO_PNG_ONLY,
    minWidth: 256,
    maxWidth: 4096,
    minHeight: 256,
    maxHeight: 4096,
    maxSizeBytes: MAX_SIZE_LOGO,
    purpose:
      'Optional wide (non-square) mark, used on the LOGIN screen only. Leave it empty and the square Logo is used there too.'
  },
  'main-logo-dark': {
    name: 'main-logo-dark',
    label: 'Login logo (wide, dark)',
    kind: 'runtime',
    delivery: 'live',
    required: false,
    acceptedMimeTypes: MIME_LOGO_PNG_ONLY,
    minWidth: 256,
    maxWidth: 4096,
    minHeight: 256,
    maxHeight: 4096,
    maxSizeBytes: MAX_SIZE_LOGO,
    purpose:
      'Optional dark-background variant of the wide login mark. Falls back to the wide light mark, then to the square Logo.'
  },
  // NOTE: there are deliberately no `home-bg-*` slots. Home backgrounds are
  // school-admin-owned and live in APP_CONFIG at `assets.backgrounds.home`,
  // written through `PUT /v1/schools/:id/app-config/assets` and edited at
  // Manage App → Design → Backgrounds. Slots for them existed until U24 but had
  // no runtime consumer after SCH-956 un-projected them, so an admin could
  // upload a background, see it succeed, and change nothing. Do not re-add
  // them: two owners for one image is what broke here the first time.
  'onboard-1': {
    name: 'onboard-1',
    kind: 'runtime',
    delivery: 'live',
    required: true,
    acceptedMimeTypes: MIME_RASTER,
    minWidth: 750,
    maxWidth: 4096,
    minHeight: 750,
    maxHeight: 4096,
    maxSizeBytes: MAX_SIZE_BG,
    purpose: 'Onboarding background — slide 1'
  },
  'onboard-2': {
    name: 'onboard-2',
    kind: 'runtime',
    delivery: 'live',
    required: true,
    acceptedMimeTypes: MIME_RASTER,
    minWidth: 750,
    maxWidth: 4096,
    minHeight: 750,
    maxHeight: 4096,
    maxSizeBytes: MAX_SIZE_BG,
    purpose: 'Onboarding background — slide 2'
  },
  'onboard-3': {
    name: 'onboard-3',
    kind: 'runtime',
    delivery: 'live',
    required: true,
    acceptedMimeTypes: MIME_RASTER,
    minWidth: 750,
    maxWidth: 4096,
    minHeight: 750,
    maxHeight: 4096,
    maxSizeBytes: MAX_SIZE_BG,
    purpose: 'Onboarding background — slide 3'
  },
  'login-bg': {
    name: 'login-bg',
    kind: 'runtime',
    delivery: 'live',
    required: false,
    acceptedMimeTypes: MIME_RASTER,
    // Westminster's shipped login-bg.jpg is 2344×5064 (same tall full-bleed
    // crop as the splash). Height cap raised 4096 → 6000 to fit reality plus
    // headroom; width cap stays 4096 (observed 2344).
    minWidth: 750,
    maxWidth: 4096,
    minHeight: 750,
    maxHeight: 6000,
    maxSizeBytes: MAX_SIZE_BG,
    purpose: 'Login screen background, optional'
  },

  // ===== Store slots =====
  //
  // Neither baked into the binary (`build`) nor fetched by the app (`runtime`).
  // A store slot is art for a STORE LISTING: it is stored here so the platform
  // has one canonical original instead of a file on an operator's laptop, and
  // it never reaches a device. `RUNTIME_SLOT_NAMES` filters `kind === 'runtime'`
  // and the mobile snapshot reads its own fixed `SLOT_SHAPES` list, so the
  // exclusion from every device path follows from the kind — do not add store
  // slots to either list.
  'play-feature-graphic': {
    name: 'play-feature-graphic',
    label: 'Google Play feature graphic',
    kind: 'store',
    delivery: 'store-listing',
    required: false,
    acceptedMimeTypes: MIME_STORE_GRAPHIC,
    // EXACT 1024×500. Google Play's preview-asset spec states one size, not a
    // range, and rejects anything else outright. No cropper stands in front of
    // this slot (SCH-1866 Key Decisions: upload as drawn), so the exact bounds
    // are what actually refuses a wrong-sized file.
    minWidth: 1024,
    maxWidth: 1024,
    minHeight: 500,
    maxHeight: 500,
    requireOpaque: true,
    maxSizeBytes: MAX_SIZE_STORE_GRAPHIC,
    purpose:
      'Google Play listing feature graphic (1024×500, no transparency). Shown on the Play listing only — never in the app and never in a build. Sent to Play by the Android setup "Store graphics" step.'
  }
} as const satisfies Record<string, AssetSlotMetadata>

export type MobileAppAssetSlotName = keyof typeof MOBILE_APP_ASSET_SLOTS

export const MOBILE_APP_ASSET_SLOT_LIST: ReadonlyArray<AssetSlotMetadata> = Object.values(MOBILE_APP_ASSET_SLOTS)

export const RUNTIME_SLOT_NAMES: ReadonlyArray<MobileAppAssetSlotName> = MOBILE_APP_ASSET_SLOT_LIST.filter(
  s => s.kind === 'runtime'
).map(s => s.name as MobileAppAssetSlotName)

export const REQUIRED_SLOT_NAMES: ReadonlyArray<MobileAppAssetSlotName> = MOBILE_APP_ASSET_SLOT_LIST.filter(
  s => s.required
).map(s => s.name as MobileAppAssetSlotName)

export function isMobileAppAssetSlotName(value: string): value is MobileAppAssetSlotName {
  return value in MOBILE_APP_ASSET_SLOTS
}

/**
 * The four pre-login image slots, in slide order followed by the login
 * background.
 *
 * These are the slots the PUBLIC onboarding-config response carries as URLs
 * (plan 2026-09-01-001, KTD5). The app reads them before login, so they cannot
 * travel through `appConfig.assets`, which is an authenticated read.
 */
export const PRE_LOGIN_ASSET_SLOT_NAMES = ['onboard-1', 'onboard-2', 'onboard-3', 'login-bg'] as const
export type PreLoginAssetSlotName = (typeof PRE_LOGIN_ASSET_SLOT_NAMES)[number]

/**
 * The allowlist a school admin may write through the school-scoped asset lane
 * (`POST /v1/schools/:school_id/mobile-app-assets/*`). Same tuple as the
 * pre-login slots by construction, so build slots (`app-icon`, `splash`,
 * `adaptive-icon-foreground`) and the logo slots are never reachable from a
 * school surface (KTD13). Widening this widens what a school can change about
 * its own binary: decide that before adding a name here.
 */
export const SCHOOL_EDITABLE_ASSET_SLOTS = PRE_LOGIN_ASSET_SLOT_NAMES
export type SchoolEditableAssetSlotName = PreLoginAssetSlotName

/** Whether a school admin may write this slot through the school-scoped lane. */
export function isSchoolEditableAssetSlotName(value: string): value is SchoolEditableAssetSlotName {
  return (SCHOOL_EDITABLE_ASSET_SLOTS as readonly string[]).includes(value)
}

/**
 * The build slots a school admin may DRAFT (release lines U8, KTD4): the app
 * icon, the splash image and the Android adaptive icon. Not a widening of
 * {@link SCHOOL_EDITABLE_ASSET_SLOTS}: the live commit route keeps refusing
 * these, and a school reaches them only through the draft routes, which write
 * `MobileAppFieldDraft` and never the slot row. Staff copy a draft into the
 * slot at release. Each name is also a `BUILD_DRAFT_FIELDS` key.
 */
export const SCHOOL_DRAFT_ASSET_SLOTS = ['app-icon', 'splash', 'adaptive-icon-foreground'] as const
export type SchoolDraftAssetSlotName = (typeof SCHOOL_DRAFT_ASSET_SLOTS)[number]

/** Whether a school admin may draft this slot through the school draft routes. */
export function isSchoolDraftAssetSlotName(value: string): value is SchoolDraftAssetSlotName {
  return (SCHOOL_DRAFT_ASSET_SLOTS as readonly string[]).includes(value)
}

/**
 * One committed pre-login image, ready to render. The manifest entry shape
 * minus `mimeType`, which the renderer has no use for: `url` is the canonical
 * versioned Cloudinary delivery URL and the app sizes it on device.
 */
export const PreLoginAssetEntrySchema = z.object({
  url: z.string().url(),
  version: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable()
})
export type PreLoginAssetEntry = z.infer<typeof PreLoginAssetEntrySchema>

/**
 * The pre-login images a device may render, keyed by slot name (KTD10).
 *
 * Keyed rather than indexed by slide, so a slide with no committed image is
 * simply absent instead of a null hole every caller has to pad. The public
 * endpoint ALWAYS emits this object (empty when nothing is committed); the
 * response marks it optional only so a new binary reading an older backend
 * still parses the body. A plain `z.object`: not strict, not passthrough, and
 * not a record, so an unknown key is stripped rather than forwarded, and a slot
 * that leaves the pre-login set cannot keep reaching devices through here.
 */
export const PreLoginAssetsSchema = z.object({
  'onboard-1': PreLoginAssetEntrySchema.optional(),
  'onboard-2': PreLoginAssetEntrySchema.optional(),
  'onboard-3': PreLoginAssetEntrySchema.optional(),
  'login-bg': PreLoginAssetEntrySchema.optional()
})
export type PreLoginAssets = z.infer<typeof PreLoginAssetsSchema>

/**
 * What committing this slot actually does for a device.
 *
 * Takes a plain `string`, not a `MobileAppAssetSlotName`, because its main
 * caller is the per-app history — and a stored event can name a slot that has
 * since left the inventory (`home-bg-1`/`home-bg-2` were removed by U24 but
 * their events are append-only and stay in the timeline forever). Those resolve
 * `unknown`: honest about not knowing, rather than guessing `none` and stating a
 * consequence for a slot whose consumer nobody can check any more.
 */
export function assetSlotDeliveryFor(slotName: string): AssetSlotDeliveryVerdict {
  return isMobileAppAssetSlotName(slotName) ? MOBILE_APP_ASSET_SLOTS[slotName].delivery : 'unknown'
}

/**
 * Reader-facing consequence per delivery verdict. Lives beside the data so the
 * timeline, the assets panel, and any later agent-facing surface state the same
 * thing — the whole point of the label is that two surfaces cannot disagree
 * about whether a change shipped.
 */
export const ASSET_SLOT_DELIVERY_LABELS: Record<AssetSlotDeliveryVerdict, { label: string; detail: string }> = {
  live: {
    label: 'Live on devices',
    detail:
      'Read at runtime — via app config, the pre-login logo cache, or the public pre-login config (app 1.9 and later); no rebuild needed.'
  },
  ota: {
    label: 'Needs an app update',
    detail:
      'Carried by the app bundle — reaches installed apps with the next over-the-air update on their release line.'
  },
  'store-build': {
    label: 'Needs a store build',
    detail: 'Baked into the binary — reaches devices only through a new store release.'
  },
  none: {
    label: 'No effect on devices',
    detail: 'Stored, but nothing reads this slot — no runtime or build-time consumer.'
  },
  'store-listing': {
    label: 'Store listing only',
    detail:
      'Stored for the store listing — it never reaches a device or a build. School Journey sends it to Google Play with the listing when it releases the app.'
  },
  unknown: {
    label: 'Effect unknown',
    detail: 'This slot is no longer part of the asset inventory.'
  }
}

export const MobileAppAssetSlotSchema = z.object({
  id: z.string(),
  mobileAppId: z.string(),
  slotName: z.string(),
  cloudinaryPublicId: z.string().nullable(),
  cloudinaryVersion: z.string().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  uploadedAt: z.string().datetime().nullable(),
  uploadedByUserId: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
})
export type MobileAppAssetSlot = z.infer<typeof MobileAppAssetSlotSchema>

export const MobileAppAssetManifestEntrySchema = z.object({
  url: z.string().url(),
  version: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  mimeType: z.string().nullable(),
  // Optional and additive: the slot row records `sizeBytes` at commit time
  // (`AssetCommitRequestSchema`) but the manifest projection does not emit it
  // yet. The snapshot step (plan 2026-09-01-2310 KTD4) fails a download whose
  // byte count disagrees with the DECLARED size; it reads this field when the
  // backend serves one and falls back to the response's `Content-Length`
  // otherwise, so declaring it here is what lets that check tighten the moment
  // the projection starts carrying it.
  sizeBytes: z.number().int().nonnegative().nullable().optional()
})
export type MobileAppAssetManifestEntry = z.infer<typeof MobileAppAssetManifestEntrySchema>

export const MobileAppAssetManifestSchema = z.object({
  mobileAppId: z.string(),
  slots: z.record(z.string(), MobileAppAssetManifestEntrySchema)
})
export type MobileAppAssetManifest = z.infer<typeof MobileAppAssetManifestSchema>

// --- Upload signature ---

export const AssetUploadSignatureRequestSchema = z.object({
  slotName: z.string().min(1)
})
export type AssetUploadSignatureRequest = z.infer<typeof AssetUploadSignatureRequestSchema>

export const AssetUploadSignatureResponseSchema = z.object({
  signature: z.string(),
  timestamp: z.number().int(),
  apiKey: z.string(),
  cloudName: z.string(),
  folder: z.string(),
  uploadPreset: z.string(),
  /**
   * `SCHOOL_UPLOAD_GUARD`, signed on every lane since SCH-3729: post both as
   * `overwrite` / `unique_filename` whenever present, or Cloudinary refuses the
   * upload. Optional only for a response from an older backend.
   */
  overwrite: z.literal(false).optional(),
  uniqueFilename: z.literal(true).optional()
})
export type AssetUploadSignatureResponse = z.infer<typeof AssetUploadSignatureResponseSchema>

/**
 * The upload parameters every signature pins (release lines U8 review; the
 * staff lanes too since SCH-3729): the upload may never replace an existing
 * Cloudinary asset, and it always gets a fresh name. Both are part of the
 * signed payload, so the browser posts them exactly as returned
 * (`overwrite=false`, `unique_filename=true`). Configuration history keeps
 * every image as public id + version, which is only durable while no upload
 * can overwrite one.
 */
export const SCHOOL_UPLOAD_GUARD = { overwrite: false, uniqueFilename: true } as const
export type SchoolUploadGuard = typeof SCHOOL_UPLOAD_GUARD

/**
 * A school-lane signature: the staff shape plus the pinned guard and the exact
 * `tags` string that was signed.
 */
export const SchoolAssetUploadSignatureResponseSchema = AssetUploadSignatureResponseSchema.extend({
  overwrite: z.literal(false),
  uniqueFilename: z.literal(true),
  tags: z.string()
})
export type SchoolAssetUploadSignatureResponse = z.infer<typeof SchoolAssetUploadSignatureResponseSchema>

// --- Commit ---

export const AssetCommitRequestSchema = z.object({
  cloudinaryPublicId: z.string().min(1),
  cloudinaryVersion: z.string().min(1),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  mimeType: z.string().min(1),
  sizeBytes: z.number().int().nonnegative()
})
export type AssetCommitRequest = z.infer<typeof AssetCommitRequestSchema>

export const AssetCommitResponseSchema = MobileAppAssetSlotSchema
export type AssetCommitResponse = z.infer<typeof AssetCommitResponseSchema>

// --- Initialize (orchestrator-callable internal endpoint) ---

export const AssetInitializeRequestSchema = z.object({}).strict().optional()
export type AssetInitializeRequest = z.infer<typeof AssetInitializeRequestSchema>

export const AssetInitializeResponseSchema = z.object({
  mobileAppId: z.string(),
  slots: z.array(MobileAppAssetSlotSchema)
})
export type AssetInitializeResponse = z.infer<typeof AssetInitializeResponseSchema>
